import {EventBus, HasEvents} from '@mintdata-wsp/common/bus/EventBus';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {AbstractSheetController} from '@mintdata-wsp/common/controllers/spreadsheet/AbstractSheetController';
import {EvalFormulaResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/EvalFormulaResult';
import {FormulaEngine} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaEngine';
import {
    OhmParseInputResult,
    OhmParseResult
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {SurfaceContextImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SurfaceContextImpl';
import {TimerContextImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/TimerContextImpl';
import {CellIOContextImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/CellIOContextImpl';
import {FunctionResolverImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/FunctionResolverImpl';
import {SheetIOContextImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/SheetIOContextImpl';
import {IMeshContext} from '@mintdata-wsp/common/controllers/spreadsheet/mesh/contexts/MeshContext';
import {MeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/MeshCellValueFormat';
import {IMeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormat';
import {MeshCellValueFormatKind} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormatKind';
import {ILinearFormulaToken} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {
    CellNotesSortOrder,
    IOrderedMeshCellNote
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellNote';
import {
    CellSettingsSortOrder,
    IOrderedMeshCellSettings
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshSheetSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSheetSize';
import {NestedFrameSlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/nestedFrameSlot/NestedFrameSlot';
import {NestedSheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NestedSheet';
import {Sheet, SlotSortOrder} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {
    DEFAULT_COL_WIDTH,
    DEFAULT_ROW_HEIGHT
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotConstants';
import {SlotStatus} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotStatus';
import {
    EMPTY_SLOT_FORMULA,
    SlotDependencies,
    SlotFormula
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotGlobalKey, SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {SlotUtil} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotUtil';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {ISheetLocation} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {SheetId, SlotId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {BaseValue} from '@mintdata/mint-kit/api/values/BaseValue';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import bind from 'bind-decorator';
import {action, reaction, runInAction} from 'mobx';

export type SlotControllerEventsListener = {
    onSlotValueChanged(slot:Slot, newValue:IValue):void
}

export class SheetController extends AbstractSheetController implements HasEvents<SlotControllerEventsListener>
{
    @bind
    public getSlots(sheetId:SheetId):Slot[]
    {
        const {main} = commonControllers.spreadsheets;
        const sheet = main.findAnySheetById(sheetId, true) as Sheet;

        return sheet.getSlots();
    }

    @bind
    public getOrCreateSlot(sheetId:SheetId, slotLocation:SlotLocation):Slot
    {
        let slot = this.findSlot(sheetId, slotLocation);

        if( !slot )
        {
            const sheet = commonControllers.spreadsheets.main.findAnySheetById(sheetId, true) as Sheet;

            this._setSlotOnSheet(
                sheetId,
                Slot.create(sheet, EMPTY_SLOT_FORMULA, slotLocation)
            );

            slot = this.findSlot(sheetId, slotLocation);

            this.initializeSlot(slot);
            setTimeout(() => this._evaluateSlot(slot));
        }

        return slot;
    }

    @bind
    public findSlotById(sheetId:SheetId, slotId:SlotId):Slot | null
    {
        const sheet = commonControllers.spreadsheets.main.findAnySheetById(sheetId, true) as Sheet;
        return (sheet && sheet.slots.get(slotId) as Slot) || null;
    }

    @bind
    public findSlot(sheetId:SheetId, slotLocation:SlotLocation):Slot | null
    {
        const sheet = commonControllers.spreadsheets.main.findAnySheetById(sheetId, true) as Sheet;
        return (sheet && sheet.getSlotByLocation(slotLocation)) || null;
    }

    @bind
    public getSlotStatus(sheetId:SheetId, slotLocation:SlotLocation):SlotStatus | null
    {
        const {flowSimulation} = commonControllers;

        const slot = this.findSlot(sheetId, slotLocation);

        if( !slot )
            return null;

        if( slot.isLive )
        {
            const simulation = flowSimulation.getForSlot(slot.id);

            if( simulation )
            {
                if( flowSimulation.isStopping(simulation.id) || flowSimulation.isStarting(simulation.id) )
                {
                    return SlotStatus.Loading;
                }

                if( flowSimulation.isRunning(simulation.id) )
                {
                    return SlotStatus.Streaming;
                }
            }
        }

        return slot.status;
    }

    @bind
    public isAnySlotLoading(sheetId:SheetId):boolean
    {
        const sheet = commonControllers.spreadsheets.main.findAnySheetById(sheetId) as Sheet;

        if( sheet )
            for( const slot of sheet.getSlots() )
                if( slot.status && slot.status.isEqualTo(SlotStatus.Loading) )
                    return true;

        return false;
    }

    @action.bound
    public initializeSlot(slot:Slot):void
    {
        if( slot.formulaReactionDisposer )
            slot.formulaReactionDisposer();

        slot.formulaReactionDisposer = reaction(
            () => slot.formulaParseResult,
            async (parseResult:OhmParseResult) => this._evaluateSlot(slot),
            {
                fireImmediately: false,
                name: `formula::${slot.location.toA1String()}`
            }
        );
    }

    @action.bound
    public async terminateSlot(slot:Slot):Promise<void>
    {
        TimerContextImpl.getInstance().stopTimerForSlot(slot.id);

        // Performing flow simulation cleanup
        if( slot.isLive )
        {
            const {flow, flowSimulation} = commonControllers;
            const simulation = flowSimulation.getForSlot(slot.id);

            if( simulation )
            {
                const slotId:SlotId = slot.id;

                // todo: the race condition is possible when a stop streaming request will come later then a start stream
                await flow.stopCloudFunctionFromSlot(slotId);

                flowSimulation.remove((simulation) => simulation.slot === slot);
            }
        }

        runInAction(() =>
        {
            if( slot.formulaReactionDisposer )
                slot.formulaReactionDisposer();

            if( slot.cellIO )
            {
                slot.cellIO.dispose();

                const workbook:Workbook = slot.sheet.workbook;

                workbook.runtime.slotDependencies.delete(slot.globalKey);
                workbook.runtime.lazySlotDependencies.delete(slot.globalKey);
            }
        });
    }

    @action.bound
    public setSlotFormulas(sheetId:SheetId,
                           slotLocations:Array<SlotLocation>,
                           formulas:Array<SlotFormula>,
                           formats:Array<string>):void
    {
        const sheetSettingsCtrl = commonControllers.spreadsheets.sheetSettings;
        slotLocations.forEach((slotLocation, index) =>
        {
            this.setSlotFormula(sheetId, slotLocation, formulas[index], false);
            const meshLocation = MeshLocation.fromSlotLocation(slotLocation);
            sheetSettingsCtrl.setCellFormat(sheetId, new MeshRange(meshLocation, meshLocation),
                MeshSheetSize.singleCell(), formats[index]);
        });
    }

    @action.bound
    public setSlotFormula(sheetId:SheetId,
                          slotLocation:SlotLocation,
                          formula:SlotFormula,
                          guessFormat:boolean = true):void
    {
        const slot = this.getOrCreateSlot(sheetId, slotLocation);

        this._setSlotFormula(sheetId, slot, formula, guessFormat);
    }

    @action.bound
    public makeSlotLazy(sheetId:SheetId,
                        slotLocation:SlotLocation):void
    {
        const slot = this.findSlot(sheetId, slotLocation);

        if( !slot || typeof slot.formula !== 'string' || slot.isLazy )
            return;

        const lazyFormula:string = SheetController._makeFormulaLazy(slot.formula);

        slot.setFormula(lazyFormula);
    }

    @action.bound
    public makeSlotRegular(sheetId:SheetId,
                           slotLocation:SlotLocation):void
    {
        const slot = this.findSlot(sheetId, slotLocation);

        if( !slot || typeof slot.formula !== 'string' || !slot.isLazy )
            return;

        const regularFormula:string = SheetController._makeFormulaRegular(slot.formula);

        slot.setFormula(regularFormula);
    }

    @action.bound
    public clearSlot(sheetId:SheetId, slotLocation:SlotLocation):void
    {
        const {spreadsheets: {main}} = commonControllers;

        const sheet = main.findAnySheetById(sheetId) as Sheet;
        const slot:Slot = sheet.getSlotByLocation(slotLocation);

        if( slot )
        {
            this.terminateSlot(slot);
            sheet.deleteSlot(slot.id);
        }
    }

    @action.bound
    public clearSlots(sheetId:SheetId, meshRange:MeshRange):void
    {
        for( let col = meshRange.from.colIndex; col <= meshRange.to.colIndex; col++ )
        {
            for( let row = meshRange.from.rowIndex; row <= meshRange.to.rowIndex; row++ )
                this.clearSlot(sheetId, new SlotLocation(col, row));
        }
    }

    @action.bound
    public async reEvaluateSlot(sheetId:SheetId, slotLocation:SlotLocation):Promise<boolean>
    {
        const slot:Slot = this.findSlot(sheetId, slotLocation);

        return slot ? this._evaluateSlot(slot) : false;
    }

    @action.bound
    public async forceEvaluateSlot(sheetId:SheetId, slotLocation:SlotLocation):Promise<boolean>
    {
        const slot:Slot = this.findSlot(sheetId, slotLocation);

        return slot ? this._evaluateSlot(slot, false, true) : false;
    }

    @action.bound
    public async reEvaluateLazySlot(sheetId:SheetId, slotLocation:SlotLocation, eventFrame?:IFrame):Promise<boolean>
    {
        const {flow, flowSimulation} = commonControllers;

        const slot:Slot = this.findSlot(sheetId, slotLocation);

        if( slot.isLive )
        {
            const simulation = flowSimulation.getForSlot(slot.id);

            if( simulation && flowSimulation.isRunningOrStarting(simulation.id) )
                return flow.stopCloudFunctionFromSlot(slot.id);
        }

        return this._evaluateSlot(slot, true, false, eventFrame);
    }

    @action.bound
    public abortSlotEvaluation(sheetId:SheetId, slotLocation:SlotLocation):void
    {
        const slot:Slot = this.findSlot(sheetId, slotLocation);

        if( this.isSlotLoading(sheetId, slotLocation) )
        {
            if( slot.cellIO )
            {
                slot.cellIO.dispose();
                slot.cellIO = null;
            }

            if( slot.value === null )
            {
                if( slot.isLazy )
                    slot.setStatus(SlotStatus.ReadyToEval);
                else
                {
                    this._setSlotValueAndNotify(slot, AEmpty.Value);
                    slot.setStatus(null);
                }
            }
            else
                slot.setStatus(null);

            // todo: the race condition is possible when a stop streaming request will come later then a start stream
            if( slot.isLive )
            {
                const {flow, flowSimulation} = commonControllers;
                const simulation = flowSimulation.getForSlot(slot.id);

                if( simulation )
                    flow.stopCloudFunctionFromSlot(slot.id);
            }
        }
    }

    @action.bound
    public insertColumns(sheetId:SheetId, fromColumnIndex:number, columnsCount:number):void
    {
        this._moveSlots(
            sheetId,
            fromColumnIndex,
            null,
            columnsCount,
            0,
            null,
            null,
            null,
            null
        );

        commonControllers.spreadsheets.namedRanges.onColsInserted(sheetId, fromColumnIndex, columnsCount);
    }

    @action.bound
    public insertRows(sheetId:SheetId, fromRowIndex:number, rowsCount:number):void
    {
        this._moveSlots(
            sheetId,
            null,
            fromRowIndex,
            0,
            rowsCount,
            null,
            null,
            null,
            null
        );

        commonControllers.spreadsheets.namedRanges.onRowsInserted(sheetId, fromRowIndex, rowsCount);
    }

    @action.bound
    public insertCellsRight(sheetId:SheetId, fromColumnIndex:number, columnsCount:number):void
    {
        Assert.fail('not used yet, and not implemented yet');
    }

    @action.bound
    public insertCellsDown(sheetId:SheetId, fromColumnIndex:number, columnsCount:number):void
    {
        Assert.fail('not used yet, and not implemented yet');
    }

    @action.bound
    public deleteColumns(sheetId:SheetId, fromColumnIndex:number, columnsCount:number, keepBindings:boolean):void
    {
        this._moveSlots(
            sheetId,
            fromColumnIndex,
            null,
            -columnsCount,
            0,
            null,
            null,
            null,
            null
        );
        commonControllers.spreadsheets.namedRanges.onColsDeleted(sheetId, fromColumnIndex, columnsCount, keepBindings);
    }

    @action.bound
    public deleteRows(sheetId:SheetId, fromRowIndex:number, rowsCount:number, keepBindings:boolean):void
    {
        this._moveSlots(
            sheetId,
            null,
            fromRowIndex,
            0,
            -rowsCount,
            null,
            null,
            null,
            null
        );
        commonControllers.spreadsheets.namedRanges.onRowsDeleted(sheetId, fromRowIndex, rowsCount, keepBindings);
    }

    @bind
    public getSlotFrameValue(sheetId:SheetId, slotLocation:SlotLocation):IFrame
    {
        return this.getSlotValue(sheetId, slotLocation) as IFrame;
    }

    @bind
    public isSlotNonEmpty(sheetId:SheetId, slotLocation:SlotLocation):boolean
    {
        const hasSlot = this.hasSlot(sheetId, slotLocation);

        if( !hasSlot )
            return false;

        const slot = this.findSlot(sheetId, slotLocation);

        if( slot.formula && slot.formula.toString() && !AEmpty.Value.isEqualTo(slot.formula as IValue) )
            return true;

        const slotValue = this.getSlotValue(sheetId, slotLocation);

        return !BaseValue.isValueEmpty(slotValue);
    }

    @bind
    public getOrderedSlots(sheetId:SheetId, sortOrder:SlotSortOrder = SlotSortOrder.ASC):Slot[]
    {
        const {main} = commonControllers.spreadsheets;
        const sheet = main.findAnySheetById(sheetId) as Sheet;

        return this.orderSlots(sheet.getSlots(), sortOrder);
    }

    public orderSlots(slots, sortOrder:SlotSortOrder = SlotSortOrder.ASC):Slot[]
    {
        return slots.sort(
            (aSlot:Slot, bSlot:Slot):number =>
            {
                const a:SlotLocation = aSlot.location;
                const b:SlotLocation = bSlot.location;

                if( a.colIndex < b.colIndex )
                    return sortOrder * -1;

                if( a.colIndex > b.colIndex )
                    return sortOrder;

                if( a.rowIndex < b.rowIndex )
                    return sortOrder * -1;

                if( a.rowIndex > b.rowIndex )
                    return sortOrder;

                return 0;
            }
        );
    }

    @bind
    public isSlotFrameValue(sheetId:SheetId, slotLocation:SlotLocation):boolean
    {
        const slotValue:IValue = this.getSlotValue(sheetId, slotLocation);

        return Type.isInstanceOf(slotValue, Type.Frame);
    }

    @bind
    public getPlainFormulaTokens(formula:SlotFormula, slotLocation:SlotLocation):ILinearFormulaToken[] | null
    {
        const {formulaInput} = commonControllers.spreadsheets;
        const engine = FormulaEngine.getInstance();

        const ohmResult:OhmParseInputResult = engine.parseInputRC(formula, slotLocation);

        if( !ohmResult.isParseSuccessful )
        {
            return null;
        }

        return formulaInput.getPlainFormulaTokens(ohmResult.result);
    }

    @action.bound
    public setSlotValue(sheetId:SheetId, slotLocation:SlotLocation, value:IValue):void
    {
        const slot = this.getOrCreateSlot(sheetId, slotLocation);

        if( slot.formula === EMPTY_SLOT_FORMULA || SlotUtil.isIValue(slot.formula) )
            slot.setFormula(AEmpty.Value.isEqualTo(value) ? '' : value);
        else
            this._setSlotValueAndNotify(slot, value);
    }

    @action.bound
    public addSlotValue(sheetId:SheetId, slotLocation:SlotLocation, value:IValue):void
    {
        const slot = this.getOrCreateSlot(sheetId, slotLocation);

        this._addSlotValueAndNotify(slot, value);
    }

    @action.bound
    private _setSlotValueAndNotify(slot:Slot, newValue:IValue):void
    {
        slot.setValue(newValue);
        EventBus.fire(this, 'slotValueChanged', slot, newValue);
    }

    @action.bound
    private _addSlotValueAndNotify(slot:Slot, newValue:IValue):boolean
    {
        const added = slot.addValue(newValue);

        if( added )
            EventBus.fire(this, 'slotValueChanged', slot, slot.internalValue);

        return added;
    }

    @action.bound
    private _setSlotFormula(sheetId:SheetId,
                            slot:Slot,
                            formula:SlotFormula,
                            guessFormat:boolean = true):void
    {
        formula = SlotUtil.ensureValidFormula(formula);
        formula = this._makeFormulaLazyIfNeeded(slot, formula);

        if( formula === slot.formula )
            return;

        // stop timer when formula changed
        TimerContextImpl.getInstance().stopTimerForSlot(slot.id);

        const formulaStringified:string = Type.isInstanceOf(formula, Type.Frame) ? '' : formula.toString();

        //if formula starts with "=" leave it as it is
        if(
            !formulaStringified
            || formulaStringified.startsWith('+=')
            || formulaStringified.startsWith('=')
            || formulaStringified.startsWith('\'')
        )
        {
            slot.setFormula(formula);
            return;
        }

        const formulaAsIValue:IValue = formula as IValue;
        const sheetSettingsCtrl = commonControllers.spreadsheets.sheetSettings;
        const meshLocation:MeshLocation = MeshLocation.fromSlotLocation(slot.location);
        const cellFormat:IMeshCellValueFormat = sheetSettingsCtrl.getCellFormat(sheetId, meshLocation.asKey());

        //if cell already has assigned format - check if new value suits and apply.
        if(
            cellFormat
            && cellFormat.kind !== MeshCellValueFormatKind.General
            && cellFormat.kind !== MeshCellValueFormatKind.Image
            && cellFormat.isStringOfThisFormat(formulaStringified)
        )
        {
            slot.setFormula(cellFormat.extractValue(formulaStringified));
            return;
        }

        if( guessFormat )
        {
            const valueFormat:IMeshCellValueFormat = this._guessFormat(formulaAsIValue);
            slot.setFormula(valueFormat.extractValue(formulaStringified));

            if( valueFormat.kind !== MeshCellValueFormatKind.Number )
                sheetSettingsCtrl.setCellFormat(
                    sheetId,
                    new MeshRange(meshLocation, meshLocation),
                    MeshSheetSize.singleCell(),
                    valueFormat.id
                );

            return;
        }

        slot.setFormula(formulaAsIValue);
    }

    @action.bound
    private async _evaluateSlot(slot:Slot, isEvalLazy:boolean = false, isForce:boolean = false, eventFrame?:IFrame):Promise<boolean>
    {
        if( !slot.formulaParseResult.isParseSuccessful )
        {
            // I think this should be a status, no need to change a slot value
            this._setSlotValueAndNotify(slot, EvalErrors.InvalidFormula);
        }
        else
        {
            // Skip an evaluation after a Formula change for Lazy cell
            if( slot.formulaParseResult.isLazyFormula() && !isEvalLazy )
            {
                slot.setStatus(SlotStatus.ReadyToEval);

                const lazySlotDependencies:Set<SlotGlobalKey> = FormulaEngine.getInstance()
                    .getAllDependencies(slot.formulaParseResult.rootNode);

                slot.workbook.runtime.lazySlotDependencies.set(slot.globalKey, lazySlotDependencies);

                if( slot.value && slot.value.isError )
                {
                    // I still think we shouldn't reset a slot value after changing a lazy formula
                    this._setSlotValueAndNotify(slot, null);
                }
            }
            else
            {
                if( slot.cellIO )
                    slot.cellIO.dispose();

                slot.cellIO = new CellIOContextImpl(slot, eventFrame);

                slot.setStatus(SlotStatus.Loading);

                // TODO[ranges]: pass namedRanges properly
                const evalResult:EvalFormulaResult = await FormulaEngine.getInstance()
                    .evalFormula(
                        slot.formulaParseResult.rootNode,
                        SheetIOContextImpl.getInstance(),
                        slot.cellIO,
                        FunctionResolverImpl.getInstance(),
                        SurfaceContextImpl.getInstance()
                    );

                // evaluation has been stopped or new evaluation has been started during existing one
                if( !slot.cellIO || evalResult.cellIO !== slot.cellIO )
                {
                    if( evalResult.cellIO )
                        evalResult.cellIO.dispose();

                    return true;
                }

                if( !isEvalLazy )
                    slot.workbook.runtime.lazySlotDependencies.delete(slot.globalKey);

                slot.workbook.runtime.setSlotDependencies(slot.globalKey, evalResult.dependencies);

                const circularRefDetected = CellIOContextImpl.detectCircularRef(slot.globalKey, slot.workbook.runtime.slotDependencies);

                if( circularRefDetected )
                    this._setSlotValueAndNotify(slot, EvalErrors.CircularRef);
                else
                {
                    // A live cell resets the value after each start
                    if( slot.formulaParseResult.isAccumulative() && !slot.formulaParseResult.isLiveFormula() )
                    {
                        const added = this._addSlotValueAndNotify(slot, evalResult.value);
                        if( !added )
                            this._setSlotValueAndNotify(slot, evalResult.value);
                    }
                    else
                    {
                        if( !slot.value || slot.value.toValue() !== evalResult.value.toValue() )
                            this._setSlotValueAndNotify(slot, evalResult.value);
                    }
                }

                slot.setStatus(null);
            }
        }

        return true;
    }

    private _moveSlots(sheetId:SheetId,
                       fromColumnIndex:number,
                       fromRowIndex:number,
                       columnsCount:number,
                       rowsCount:number,
                       startColumnIndex:number,
                       startRowIndex:number,
                       endColumnIndex:number,
                       endRowIndex:number):void
    {
        const {main, mesh, sheetSettings, workbook: workbookCtrl} = commonControllers.spreadsheets;

        const sheet = main.findAnySheetById(sheetId) as Sheet;

        const meshCtx:IMeshContext = workbookCtrl.getMeshContextBySheetId(sheetId);

        const meshState = mesh.getState(meshCtx);

        const isRemoveColumns:boolean = columnsCount < 0 && startColumnIndex == null && endColumnIndex == null;
        const isRemoveRows:boolean = rowsCount < 0 && startRowIndex == null && endRowIndex == null;

        if( columnsCount > 0 )
            meshState.insertColumnWidths(fromColumnIndex, DEFAULT_COL_WIDTH, Math.abs(columnsCount));
        else if( isRemoveColumns )
            meshState.removeColumnWidths(fromColumnIndex, Math.abs(columnsCount));

        if( rowsCount > 0 )
            meshState.insertRowHeights(fromRowIndex, DEFAULT_ROW_HEIGHT, Math.abs(rowsCount));
        else if( isRemoveRows )
            meshState.removeRowHeights(fromRowIndex, Math.abs(rowsCount));

        const {size} = meshState;

        if( startColumnIndex == null )
            startColumnIndex = 0;

        if( startRowIndex == null )
            startRowIndex = 0;

        if( endColumnIndex == null )
            endColumnIndex = size.numCols - 1;

        if( endRowIndex == null )
            endRowIndex = size.numRows - 1;

        if( columnsCount == null )
            columnsCount = 0;

        if( rowsCount == null )
            rowsCount = 0;

        const shouldRemoveCell = (colIndex:number, rowIndex:number):boolean =>
        {
            return (
                (isRemoveColumns && colIndex >= fromColumnIndex && colIndex < fromColumnIndex - columnsCount) ||
                (isRemoveRows && rowIndex >= fromRowIndex && rowIndex < fromRowIndex - rowsCount)
            );
        };

        const shouldChangeCellLocation = (colIndex:number, rowIndex:number):boolean =>
        {
            return (
                (colIndex >= startColumnIndex && colIndex <= endColumnIndex) &&
                (rowIndex >= startRowIndex && rowIndex <= endRowIndex) &&
                (
                    (colIndex >= fromColumnIndex && columnsCount !== 0) ||
                    (rowIndex >= fromRowIndex && rowsCount !== 0)
                )
            );
        };

        const slotSortOrder = isRemoveColumns || isRemoveRows ? SlotSortOrder.ASC : SlotSortOrder.DESC;
        const orderedSlots:Slot[] = this.getOrderedSlots(sheetId, slotSortOrder);

        const cellSettingsSortOrder = isRemoveColumns || isRemoveRows ? CellSettingsSortOrder.ASC : CellSettingsSortOrder.DESC;
        const orderedCellSettings:IOrderedMeshCellSettings[] = sheetSettings.getOrderedCellSettings(sheetId, cellSettingsSortOrder);

        const cellNotesSortOrder = isRemoveColumns || isRemoveRows ? CellNotesSortOrder.ASC : CellNotesSortOrder.DESC;
        const orderedCellNotes:IOrderedMeshCellNote[] = sheetSettings.getOrderedCellNotes(sheetId, cellNotesSortOrder);

        orderedCellSettings.forEach(({location}) =>
        {
            const {colIndex, rowIndex} = location;

            if( (shouldRemoveCell(colIndex, rowIndex)) )
            {
                sheetSettings.removeSheetSettingsForCell(sheetId, location);
            }
            else
            {
                if( shouldChangeCellLocation(colIndex, rowIndex) )
                {
                    const newLocation:MeshLocation = new MeshLocation(colIndex + columnsCount, rowIndex + rowsCount);

                    sheetSettings.copyCellSettings(sheetId, location.asKey(), newLocation.asKey());
                    sheetSettings.removeSheetSettingsForCell(sheetId, location);
                }
            }
        });

        orderedCellNotes.forEach(({location}) =>
        {
            const {colIndex, rowIndex} = location;

            if( (shouldRemoveCell(colIndex, rowIndex)) )
            {
                sheetSettings.removeSheetSettingsForCell(sheetId, location);
            }
            else
            {
                if( shouldChangeCellLocation(colIndex, rowIndex) )
                {
                    const newLocation:MeshLocation = new MeshLocation(colIndex + columnsCount, rowIndex + rowsCount);

                    sheetSettings.copyCellNote(sheetId, location.asKey(), newLocation.asKey());
                    sheetSettings.removeSheetSettingsForCell(sheetId, location);
                }
            }
        });

        orderedSlots.forEach((slot:Slot):void =>
        {
            if( !slot )
                return;

            const slotLocation = slot.location;
            const oldMeshLocation:MeshLocation = new MeshLocation(slot.location.colIndex, slot.location.rowIndex);

            if( (shouldRemoveCell(slotLocation.colIndex, slotLocation.rowIndex)) )
            {
                this.clearSlot(sheetId, slot.location);
                sheetSettings.setCellFormat(sheetId, new MeshRange(oldMeshLocation, oldMeshLocation), MeshSheetSize.singleCell(), MeshCellValueFormat.General.id);
            }
            else
            {
                if( shouldChangeCellLocation(slotLocation.colIndex, slotLocation.rowIndex) )
                {
                    const newLocation:SlotLocation = new SlotLocation(slotLocation.colIndex + columnsCount,
                        slotLocation.rowIndex + rowsCount);

                    sheet.setSlotLocation(slot.id, newLocation);

                    const newMeshLocation:MeshLocation = new MeshLocation(slotLocation.colIndex + columnsCount,
                        slotLocation.rowIndex + rowsCount);
                    const oldFormatId = sheetSettings.getCellFormat(sheetId, oldMeshLocation.asKey()).id;
                    sheetSettings.setCellFormat(sheetId, new MeshRange(newMeshLocation, newMeshLocation), MeshSheetSize.singleCell(), oldFormatId);
                    sheetSettings.removeCellFormat(sheetId, new MeshRange(oldMeshLocation, oldMeshLocation));
                }

                if( typeof slot.formula === 'string' )
                {
                    const sc = commonControllers.spreadsheets;
                    const fic = sc.formulaInput;

                    const plainTokens = this.getPlainFormulaTokens(slot.formula, slot.location);

                    let updatedTokens:Array<ILinearFormulaToken> = fic.recalculateCellReferencesForMovingCells(
                        plainTokens,
                        slotLocation.colIndex,
                        slotLocation.rowIndex,
                        fromColumnIndex,
                        fromRowIndex,
                        columnsCount,
                        rowsCount
                    );

                    updatedTokens = fic.normalizeCellReferences(updatedTokens, slot.location.colIndex, slot.location.rowIndex);
                    const formulaR1C1:SlotFormula = fic.getFormulaTokensText(updatedTokens, slot.location.colIndex, slot.location.rowIndex, false);
                    this._setSlotFormula(sheetId, slot, formulaR1C1, false);

                    // evaluate to recalculate dependencies
                    // TODO: to find a faster way of updating dependencies, e.g.:
                    // - recalculate them without re-evaluation of the slot
                    // - check for circular refs
                    this._evaluateSlot(slot);
                }
            }
        });

        const sc = commonControllers.spreadsheets;
        const fic = sc.formulaInput;

        const workbook:Workbook = main.findWorkbookById(sheet.workbook.id);
        const dependencies:Map<SlotGlobalKey, SlotDependencies> = workbook.runtime.slotDependencies;
        const lazySlotDependencies:Map<SlotGlobalKey, Set<SlotGlobalKey>> = workbook.runtime.lazySlotDependencies;

        let slotsWithRefsToActiveSheetGlobalKeys:Set<SlotGlobalKey> = new Set<SlotGlobalKey>();

        dependencies.forEach((mapOfDependencies:SlotDependencies, slotWithExternalRefKey:SlotGlobalKey) =>
        {
            const {sheetId: sheetIdForSlotWithExternalRefKey}:ISheetLocation = SlotLocation.fromGlobalKey(slotWithExternalRefKey);

            if( sheetIdForSlotWithExternalRefKey !== sheet.id )
            {
                for( let dependedSlotGlobalKey of Array.from(mapOfDependencies.keys()) )
                {
                    const {sheetId: sheetIdForDependedSlot}:ISheetLocation = SlotLocation.fromGlobalKey(dependedSlotGlobalKey);

                    if( sheetIdForDependedSlot === sheet.id )
                    {
                        slotsWithRefsToActiveSheetGlobalKeys.add(slotWithExternalRefKey);
                    }
                }
            }
        });

        lazySlotDependencies.forEach((mapOfDependencies:Set<SlotGlobalKey>, slotWithExternalRefKey:SlotGlobalKey) =>
        {
            const {sheetId: sheetIdForSlotWithExternalRefKey}:ISheetLocation = SlotLocation.fromGlobalKey(slotWithExternalRefKey);

            if( sheetIdForSlotWithExternalRefKey !== sheet.id )
            {
                for( let dependedSlotGlobalKey of Array.from(mapOfDependencies.keys()) )
                {
                    const {sheetId: sheetIdForDependedSlot}:ISheetLocation = SlotLocation.fromGlobalKey(dependedSlotGlobalKey);

                    if( sheetIdForDependedSlot === sheet.id )
                    {
                        slotsWithRefsToActiveSheetGlobalKeys.add(slotWithExternalRefKey);
                    }
                }
            }
        });

        slotsWithRefsToActiveSheetGlobalKeys.forEach((slotGlobalKey:SlotGlobalKey):void =>
        {
            const {sheetId, columnIndex, rowIndex}:ISheetLocation = SlotLocation.fromGlobalKey(slotGlobalKey);
            const slot:Slot = this.getOrCreateSlot(sheetId, new SlotLocation(columnIndex, rowIndex));
            const plainTokens = this.getPlainFormulaTokens(slot.formula, slot.location);

            let updatedTokens:Array<ILinearFormulaToken> = fic.recalculateCellReferencesForMovingCells(
                plainTokens,
                slot.location.colIndex,
                slot.location.rowIndex,
                fromColumnIndex,
                fromRowIndex,
                columnsCount,
                rowsCount,
                true
            );

            updatedTokens = fic.normalizeCellReferences(updatedTokens, slot.location.colIndex, slot.location.rowIndex);
            const formulaR1C1:SlotFormula = fic.getFormulaTokensText(updatedTokens, slot.location.colIndex, slot.location.rowIndex, false);
            this._setSlotFormula(sheetId, slot, formulaR1C1, false);
        });
    }

    @action
    private _setSlotOnSheet(sheetId:SheetId, slot:Slot):void
    {
        if( !slot )
        {
            mk.error(area.Slot, 'addSlot: Slot should be defined');
            return;
        }

        if( !slot.location )
        {
            mk.error(area.Slot, 'addSlot: SlotLocation should be defined for Slot');
            return;
        }

        const {main} = commonControllers.spreadsheets;
        const sheet = main.findAnySheetById(sheetId) as Sheet;

        if( sheet.getSlotByLocation(slot.location) )
        {
            mk.error(area.Slot, 'addSlot: SlotLocation already defined');
            return;
        }

        sheet.addSlot(slot);
    }

    private _guessFormat(value:IValue):IMeshCellValueFormat
    {
        if( !value || value.toString() === '' )
        {
            return MeshCellValueFormat.General;
        }
        const correspondingFormat = MeshCellValueFormat.BuiltInFormats.formatsToProbe.find(val =>
        {
            return val.isStringOfThisFormat(value.toString());
        });
        if( !correspondingFormat ) return MeshCellValueFormat.General;
        //define which number format to use depending on digits after point
        return MeshCellValueFormat.getFormatDependingOnDigitsAfterPointInValue(value, correspondingFormat);
    }

    private _makeFormulaLazyIfNeeded(slot:Slot, formula:SlotFormula):SlotFormula
    {
        if( typeof formula !== 'string' )
            return formula;

        const {spreadsheets: {workbook, clientFunctions}} = commonControllers;

        let ohmResult:OhmParseResult;

        ohmResult = FormulaEngine.getInstance().parseRC(
            {
                workbookId: slot.sheet.workbook.id,
                sheetId: slot.sheet.id,
                colIndex: slot.location.colIndex,
                rowIndex: slot.location.rowIndex,
                findSheetIdByName: (workbookId:WorkbookId, sheetName:string):SheetId =>
                {
                    const sheet = workbook.findSheetByName(workbookId, sheetName);

                    return sheet ? sheet.id : null;
                }
            },
            formula
        );

        if( ohmResult.isParseSuccessful
            && !ohmResult.isLazyFormula()
            && ohmResult.rootNode
            && clientFunctions.containsLazyOnlyFunction(ohmResult)
        )
            return SheetController._makeFormulaLazy(formula);

        return formula;
    }

    private static _makeFormulaLazy(formula:string):string
    {
        const isLazyFunction = formula.startsWith('=?') || formula.startsWith('+=?');
        const isLiveFunction = formula.startsWith('=*') || formula.startsWith('+=*');
        const replacementNeeded = !isLazyFunction && !isLiveFunction;

        if( replacementNeeded )
        {
            if( formula.startsWith('=') )
                return formula.replace('=', '=?');

            if( formula.startsWith('+=') )
                return formula.replace('+=', '+=?');
        }

        return formula;
    }

    private static _makeFormulaRegular(formula:string):string
    {
        const isLazyFunction = formula.startsWith('=?') || formula.startsWith('+=?');
        const isLiveFunction = formula.startsWith('=*') || formula.startsWith('+=*');
        const replacementNeeded = isLazyFunction && !isLiveFunction;

        if( replacementNeeded )
        {
            if( formula.startsWith('=?') )
                return formula.replace('=?', '=');

            if( formula.startsWith('+=?') )
                return formula.replace('+=?', '+=');
        }

        return formula;
    }
}
